import { useState } from "react";
import { User, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AuthLayout, { LOG_IN_LINK } from "../../../components/Auth/AuthLayout";
import SignUpForm from "./SignUpForm";
import { ENTERPRISE, FREELANCE } from "../../../utils/constants/accountType";

const TABS = [
  {
    accountType: FREELANCE,
    label: "Sign Up as Freelancer",
    description: "For individual translators working on their own.",
    icon: User,
  },
  {
    accountType: ENTERPRISE,
    label: "Sign Up as Translation Agency",
    description: "For agencies managing vendors, managers and clients.",
    icon: Building2,
  },
];

const SHIFT = { type: "spring", stiffness: 300, damping: 32 };
const COLLAPSE = { duration: 0.25, ease: "easeInOut" };

const AdminRegistration = () => {
  const [accountType, setAccountType] = useState(null);

  return (
    <AuthLayout width="max-w-[560px]" topLink={LOG_IN_LINK}>
      <motion.h1
        layout="position"
        transition={SHIFT}
        className="text-center text-[28px] font-medium leading-tight text-gray-900"
      >
        Create an Account
      </motion.h1>

      {/* Between lg and xl the form column is at its narrowest, and that is the
          one width where two cards side by side would wrap the longer heading,
          so they stack there. */}
      <motion.div
        layout="position"
        transition={SHIFT}
        className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
      >
        {TABS.map(({ accountType: type, label, description, icon: Icon }) => {
          const isSelected = accountType === type;

          return (
            <button
              key={type}
              type="button"
              onClick={() => setAccountType(type)}
              className={`relative rounded-xl border px-4 py-4 text-left transition-colors ${
                isSelected
                  ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500"
                  : "border-gray-200 bg-white hover:border-brand-400 hover:bg-brand-50/40"
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon
                  className={`h-4 w-4 shrink-0 ${
                    isSelected ? "text-brand-600" : "text-gray-400"
                  }`}
                />
                <span className="whitespace-nowrap text-[13px] font-semibold text-gray-900">
                  {label}
                </span>
              </div>

              <AnimatePresence initial={false}>
                {!accountType && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={COLLAPSE}
                    className="overflow-hidden"
                  >
                    <p className="pt-2 text-xs leading-5 text-gray-500">
                      {description}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          );
        })}
      </motion.div>

      <AnimatePresence initial={false}>
        {!accountType && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={COLLAPSE}
            className="overflow-hidden"
          >
            <p className="pt-8 text-center text-sm text-gray-500">
              Choose the type of account you want to create to continue.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {accountType && (
          <motion.div
            key={accountType}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: "easeOut", delay: 0.12 }}
          >
            {/* Remount on switch so a half filled form never carries over. */}
            <SignUpForm accountType={accountType} />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.p
        layout="position"
        transition={SHIFT}
        className="mt-8 text-center text-xs font-medium text-gray-500"
      >
        Already have an account?{" "}
        <Link to="/login" className="font-bold text-brand-600 hover:underline">
          Log In
        </Link>
      </motion.p>
    </AuthLayout>
  );
};

export default AdminRegistration;